/**
 * karmabooking-api
 *
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2020 Karmabunny
 */

/// <reference types='stripe-v3'/>

import { api } from "./base";

// TODO Our link translations are currently static (.po files). But they should
// be content managed. When they are - use this.
export interface UrlJson {
    url: string;
    name: string;
}

export type BookingStatus =
    | 'editing'
    | 'processing'
    | 'error'
    | 'complete'
;

export type PaymentStatus =
    | 'pending'
    | 'received'
    | 'processed'
    | 'refunded'
    | 'error'
    | 'cancelled'
;

// karmabooking/src/providers/RoomTypeModel.php
export interface RoomTypeJson {
    property_id: string;
    room_id: number;
    room_uid: string;
    rms_config: RmsConfigItem[];
    name: string;
    short_description: string;
    long_description: string;
    not_bookable_message: string;
    floor_plan_url: string;
    images: string[];
    amenities: string[];
    links: Record<string, string>;
    is_bookable: boolean;
    category: SearchResultJson;
}

export interface RmsConfigItem {
    any?: number;
    male?: number;
    female?: number;
}

// karmabooking/src/providers/CategoryApiModel.php
export interface SearchResultJson {
    property_ref: string;
    room_ref: string;
    name: string;
    rate_id: number;
    rate_name: string;
    weeks: number;
    min_weeks: number;
    max_weeks: number;
    week_price: number;
    total_price: number;
    currency: string;
    is_available: boolean;
    actual: boolean;
    constraint: ConstraintJson;
}

export interface ConstraintJson {
    [key: string]: {
        type: string;
        require: any[];
        message: [string, any];
        text: string;
    }
}

// karmabooking/src/providers/ChargeModel.php
export interface ChargeTypeJson {
    period: number;
    price: number;
    currency: string;
}

// karmabooking/src/providers/PropertyModel.php
export interface PropertyJson {
    property_ref: string;
    name: string;
    description: string;
    more_description: string;
    links: Record<string, string>;
    rooms: RoomTypeJson[];
    icon_url: string;
    position: {
        lat: number;
        lng: number;
    }
}

// karmabooking/src/records/BookingRecord.php
export interface BookingJson {
    booking_uid: string;
    room_id?: number;
    room_type_ref?: string;
    property_ref?: string;
    status: 'editing' | 'processing' | 'error' | 'complete';
    payment_status?: null | PaymentStatus;
    created_date: string;
    updated_date: string;
    start_date?: string;
    end_date?: string;
    gender?: string;
    passport_first_name?: string;
    passport_last_name?: string;
    native_first_name?: string;
    native_last_name?: string;
    preferred_name?: string;
    date_of_birth?: string;
    nationality?: string;
    email_address?: string;
    mobile_number?: string;
    address?: string;
    languages?: string[];
    prefer_bed_type?: string;
    prefer_floor?: string;
    prefer_languages?: string[];
    marketing_source?: string;
    marketing_action?: string;
    marketing_notes?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    emergency_contact_email?: string;
    emergency_contact_address?: string;
    emergency_contact_relationship?: string;
    terms_agreed?: boolean;
}

export interface BookingRatesJson {
    booking_uid: string;
    room_type_ref: string;
    charges: ChargeTypeJson[];
}

// karmabooking/src/controllers/BookingApiController.php
export interface BookingConfirmJson {
    booking_uid: string;
    booking_reference?: string;
    booking_status: BookingStatus;
    booking_complete: boolean;
    payment_uid: string;
    payment_status: PaymentStatus;
    stripe_public_key: string;
}

// karmabooking/src/controllers/BookingApiController.php
export interface BookingBookedJson {
    booking_uid: string;
    booking_reference?: string;
    booking_status: BookingStatus;
    booking_complete: boolean;
}

// karmabooking/src/controllers/PaymentApiController.php
export interface PaymentJson {
    payment_uid: string;
    payment_status: PaymentStatus;
    payment_intent_id: string;
    payment_intent_status: string; // TODO string union
    payment_intent_secret: string;
    requires_action: boolean;
}

export type PaymentProcessJson = {
    payment_uid: string;
} & ({
    payment_method_id: string;
    payment_intent_id?: undefined;
} | {
    payment_method_id?: undefined;
    payment_intent_id: string;
});

 /**
  *
  * @param start_date
  * @param end_date
  * @param gender
  */
export async function getSearch(
        property_ref: string,
        start_date: string,
        end_date: string,
        gender: string,
    ) {

    return await api({
        method: "GET",
        endpoint: "/kbapi/search",
        query: {
            property_ref,
            start_date,
            end_date,
            gender,
        }
    })
    .then(res => res.rooms as RoomTypeJson[]);
}


/**
 *
 */
export async function getProperties() {
    return await api({ method: "GET", endpoint: "/kbapi/property" })
    .then(res => res.properties as PropertyJson[]);
}


/**
 *
 */
export async function getCsrf() {
    return await api({ method: "GET", endpoint: "/kbapi/csrf" })
    .then(res => res.csrf_token as string);
}


/**
 *
 */
export async function getCountryNames(lang?: string) {
    return await api({
        method: "GET",
        endpoint: "/book/country_names.json",
        query: { lang },
    })
    .then(res => res.countries as Record<string, string>);
}

/**
 *
 */
export async function getCountryName(code: string, lang?: string) {
    return await api({
        method: "GET",
        endpoint: "/book/country_name.json",
        query: { code, lang },
    })
    .then(res => res.country_name as string);
}


export async function createBooking() {
    return await api({
        method: "POST",
        endpoint: "/kbapi/booking/new",
    })
    .then(res => res.booking_uid as string);
}


export async function getBooking(booking_uid: string) {
    return await api({
        method: "GET",
        endpoint: `/kbapi/booking/${booking_uid}`
    }) as Promise<BookingJson>;
}


export async function getBookingRates(booking_uid: string) {
    return await api({
        method: "GET",
        endpoint: `/kbapi/booking/${booking_uid}/rates`
    }) as Promise<BookingRatesJson>;
}


export async function editBooking(booking_uid: string, booking: Partial<BookingJson>) {
    return await api({
        method: "PUT",
        endpoint: `/kbapi/booking/${booking_uid}/edit`,
        body: JSON.stringify(booking),
    }) as Promise<Partial<BookingJson>>;
}


export async function deleteBooking(booking_uid: string) {
    return await api({
        method: "DELETE",
        endpoint: `/kbapi/booking/${booking_uid}/delete`
    })
    .then(res => res.booking_uid as string);
}


export async function processBooking(booking_uid: string) {
    return await api({
        method: "POST",
        endpoint: `/kbapi/booking/${booking_uid}/book`,
    }) as Promise<BookingBookedJson>;
}


export async function completeBooking(booking_uid: string) {
    return await api({
        method: "POST",
        endpoint: `/kbapi/booking/${booking_uid}/confirm`,
    }) as Promise<BookingConfirmJson>;
}


export async function processPayment(payment: PaymentProcessJson) {
    return await api({
        method: "POST" ,
        endpoint: "/kbapi/payments/process",
        body: JSON.stringify(payment),
    }) as Promise<PaymentJson>;
}
